import { useEffect, useState } from 'react';

/**
 * Whether the Bizy mascot's perpetual ambient animations (wing flutter,
 * antenna sway, hover bob, blink) should be running.
 *
 * Those animations loop forever via Framer Motion. The browser already stops
 * them when the tab/window is fully hidden, but a *visible yet unfocused*
 * window — Bizy left open behind other windows or on another desktop — keeps
 * compositing every frame, burning CPU and battery for a mascot nobody is
 * looking at (BIZY-661). Treat "active" as document visible AND window focused
 * so callers can freeze the loop the moment attention leaves and resume it the
 * moment it returns.
 */
const readActive = (): boolean => {
	if (typeof document === 'undefined') {
		return true;
	}

	return document.visibilityState === 'visible' && document.hasFocus();
};

export const useBizyMascotActive = (): boolean => {
	const [active, setActive] = useState(readActive);

	useEffect(() => {
		const update = () => setActive(readActive());

		window.addEventListener('focus', update);
		window.addEventListener('blur', update);
		document.addEventListener('visibilitychange', update);
		// Sync once on mount in case focus/visibility changed before we subscribed.
		update();

		return () => {
			window.removeEventListener('focus', update);
			window.removeEventListener('blur', update);
			document.removeEventListener('visibilitychange', update);
		};
	}, []);

	return active;
};
